import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import Select from "react-select";
import * as api from "../../../../backend/request";
import {
  getUserDetails,
  getUserRole,
  hasUserAccess,
} from "../../../../backend/auth";
import { toast } from "react-toastify";
import PageLoader from "../../../shared/PageLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Link, usePath, useHistory, navigate } from "raviger";
import Table, {
  CellTmpl,
  DEFAULT_PAYLOAD,
  TitleTmpl,
  reactFormatter,
} from "../../../common/Table";
import {
  STATUS_MAPPING,
  TOOLTIP_DELAY,
  MONTHS,
} from "../../../common/Constants";
import {
  useGetAllVendorAudits,
} from "../../../../backend/query";
import Icon from "../../../common/Icon";
import {
  downloadFileContent,
  preventDefault
} from "../../../../utils/common";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import AdminVendorLocations from "../../Masters/Companies/AdminVendorLocation";
import VendorAuditBulkDownloadModal from "./VendorAuditBulkDownloadModal";
import { getAuditReportFileName, useAuditReportForVendor } from "../../../../backend/exports";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import { AUDIT_STATUS_COLOR_CODES, AUDIT_STATUS_LABELS, AUDIT_STATUS as VENDOR_AUDIT_STATUS } from "../../../../utils/auditStateMachine";
import DropAuditModal from "./DropAuditModal";


const ALL_OPTION = { value: "all", label: "ALL" };

const _currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 10 }, (_, i) => ({
  value: _currentYear - i,
  label: String(_currentYear - i),
}));

const AUDIT_STATUS_FILTER_OPTIONS = [
  {
    value: "scheduled",
    label: "Scheduled Audits",
    statuses: ["preliminary_audit_pending"],
  },
  {
    value: "in_progress",
    label: "Preliminary In Progress",
    statuses: [
      "preliminary_audit_submitted",
      "preliminary_audit_report_uploading",
      "preliminary_audit_report_failed",
    ],
  },
  {
    value: "preliminary_completed",
    label: "Preliminary Audit Completed",
    statuses: [
      "final_audit_pending",
      "final_audit_editing",
      "final_audit_submitted",
      "final_audit_report_uploading",
      "final_audit_report_failed",
    ],
  },
  {
    value: "final_completed",
    label: "Final Audit Completed",
    statuses: ["final_audit_published", "final_audit_report_uploaded"],
  },
  {
    value: "dropped",
    label: "Dropped Audit",
    statuses: ["dropped_audit"],
  },
];

const SortFields: any = {
  "company.name": "company_name",
  "associate_company.name": "associate_company_name",
  "location.name": "location_name",
};

function VendorAuditList() {
  const { state }: any = useHistory();
  const path: any = usePath();
  const [fromDashboard] = useState(path.includes("/dashboard/activities"));
  const [data, setData] = useState<any>();
  const [params, setParams] = useState<any>();
  const [locationFilters, setLocationFilter] = useState<any>(
    state && state.initialFilters ? state.initialFilters : undefined
  );
  const lfRef: any = useRef();
  lfRef.current = locationFilters;
  const [payload, setPayload] = useState<any>();
  const payloadRef = useRef();
  payloadRef.current = payload;
  const [showLoader, setShowLoader] = useState(false);
  const [previousFilters, setPreviousFilters] = useState<any>(state || {});
  const previousFiltersRef = useRef();
  previousFiltersRef.current = previousFilters;
  const [enableBulkDownloadModal, setEnableBulkDownloadModal] = useState<boolean>(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [dropAudit, setDropAudit] = useState<any>(null);
  const initialAuditStatus = state && state.auditStatusFilter
    ? state.auditStatusFilter
    : (state && state.auditStatus ? { [state.auditStatus]: true } : {});
  const [auditStatusFilter, setAuditStatusFilter] = useState<any>(initialAuditStatus);
  const auditStatusRef = useRef<any>(initialAuditStatus);
  auditStatusRef.current = auditStatusFilter;

  const initialMonth = state && state.month
    ? MONTHS.find((m: any) => m.value === state.month) || ALL_OPTION
    : ALL_OPTION;
  const initialYear = state && state.year
    ? yearOptions.find((y: any) => y.value === state.year) || ALL_OPTION
    : ALL_OPTION;
  const [monthFilter, setMonthFilter] = useState<any>(initialMonth);
  const [yearFilter, setYearFilter] = useState<any>(initialYear);
  const monthRef = useRef<any>(initialMonth);
  const yearRef = useRef<any>(initialYear);
  monthRef.current = monthFilter;
  yearRef.current = yearFilter;
  const { activities, total, isFetching, refetch } = useGetAllVendorAudits(
    payload,
    Boolean(hasFilters(null, "company_id_eq") || hasFilters(null, "company_id_in"))
  );
  const { auditReportVendor, exporting } = useAuditReportForVendor(
      (response: any) => {
        const filename = selectedReport && selectedReport.name ? selectedReport.name.charAt(0).toUpperCase() + selectedReport.name.slice(1) : 'audit_report';
        if (response) {
          downloadFileContent({
            name: filename,
            type: response.headers["content-type"],
            content: response.data,
          });
        }
      },
      (error: any) => {
        console.log(error)
        toast.error(ERROR_MESSAGES.DEFAULT);
      }
    );

  function onLocationChange(event: any, filter: any = {}) {
    // setData([]);
    setLocationFilter(event);
    if(Object.keys(filter).length > 0){
      const _filter = {
        ...previousFilters,
        ...filter
      }
      setPreviousFilters(_filter);
    }
  }

  async function downloadReport(audit: any) {
    if (audit && audit.id) {
      setShowLoader(true);
      try {
        let reportData: any;
        let auditData: any;
        if (audit && (audit.preliminary_report || audit.final_report)) {
          auditData = audit;
        } else {
          const response = await api.get(`/api/v1/vendor_location_audits/${audit.id}`, {
            include: ['preliminary_report', 'final_report'],
            includes: ['preliminary_report', 'final_report']
          });
          auditData = response.data;
        }
        if([VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_FINALIZED, VENDOR_AUDIT_STATUS.FINAL_AUDIT_PENDING, VENDOR_AUDIT_STATUS.FINAL_AUDIT_SUBMITTED, VENDOR_AUDIT_STATUS.FINAL_AUDIT_EDITING].includes(audit.status)){
          reportData = auditData.preliminary_report;
        } else if([VENDOR_AUDIT_STATUS.FINAL_AUDIT_PUBLISHED, VENDOR_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED].includes(audit.status)){
          reportData = auditData.final_report;
        } else if(audit.status === VENDOR_AUDIT_STATUS.DROPPED_AUDIT){
          reportData = auditData.final_report || auditData.preliminary_report;
        }
        setSelectedReport(reportData);

        if (!reportData) {
          toast.warn(`No report available`);
          setShowLoader(false);
          return;
        }

        const { s3_region, s3_key, s3_bucket } = reportData || {};
        if (s3_region && s3_key && s3_bucket) {
          auditReportVendor({
            s3_region,
            s3_key,
            s3_bucket
          });
        } else {
          toast.warn(`No report available`);
        }
      } catch (error) {
        console.error('Error fetching audit:', error);
      }
      setShowLoader(false);
    }
  }

  function MonthTmpl({ cell }: any) {
    const row = cell.getData();
    return (
      <div className="d-flex align-items-center position-relative h-100">
        {row.month} ({row.year})
      </div>
    );
  }

  function ActionColumnElements({ cell }: any) {
    const row = cell.getData();
    const isDropped = row.status === VENDOR_AUDIT_STATUS.DROPPED_AUDIT;
    const isFinalCompleted = row.status === VENDOR_AUDIT_STATUS.FINAL_AUDIT_PUBLISHED || row.status === VENDOR_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED;
    const canDropAudit = hasUserAccess(USER_PRIVILEGES.REVIEWER_VENDOR_ACTIVITIES_DROP_AUDIT);
    return (
      <div className="d-flex flex-row align-items-center position-relative h-100 gap-2" onClick={(e) => e.stopPropagation()}>
        <Icon
          className="ms-1"
          type="button"
          name="download"
          text="Download"
          data={row}
          action={downloadReport}
        />
        {canDropAudit && !isDropped && !isFinalCompleted && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`drop-tooltip-${row.id}`}>Drop Audit</Tooltip>}
          >
            <span
              style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
              onClick={() => setDropAudit(row)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2.5" y="2.5" width="11" height="11" stroke="#C00F0C"/>
                <path d="M11 5L5 11M5 5L11 11" stroke="#C00F0C" strokeLinecap="square" strokeLinejoin="round"/>
              </svg>
            </span>
          </OverlayTrigger>
        )}
      </div>
    );
  }

  const columns = [
    {
      title: "Month & Year",
      field: "month",
      headerSort: false,
      width: 140,
      formatter: reactFormatter(<MonthTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      headerClick: (e: any, col: any) => {
        preventDefault(e);
      },
    },
    {
      title: "Company",
      field: "company.name",
      headerSort: false,
      widthGrow: 2,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Associate Company",
      field: "associate_company.name",
      headerSort: false,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      widthGrow: 1,
    },
    {
      title: "Location Name",
      field: "location.name",
      headerSort: false,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      widthGrow: 1,
    },
    {
      title: "Vendor Category",
      field: "vendor_category.name",
      headerSort: false,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      widthGrow: 1,
    },
    {
      title: "Vendor",
      field: "vendor_registration.vendor_name",
      headerSort: false,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      widthGrow: 1,
    },
    {
      title: "Status",
      field: "status",
      headerSort: false,
      formatter: reactFormatter(<AuditStatusTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      widthGrow: 1
    },
    {
      title: "Actions",
      hozAlign: "center",
      width: 120,
      headerSort: false,
      formatter: reactFormatter(<ActionColumnElements />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
  ];

  function AuditStatusTmpl({ cell }: any) {
    const status: any = cell.getValue();
    return (
      <div className="d-flex align-items-center position-relative h-100">
        {status && (
          <OverlayTrigger overlay={<Tooltip>{AUDIT_STATUS_LABELS[status as keyof typeof AUDIT_STATUS_LABELS] || status}</Tooltip>} rootClose={true}
              placement="bottom" delay={{ show: TOOLTIP_DELAY } as any}>
              <div className={`ellipse two-lines ${AUDIT_STATUS_COLOR_CODES[status as keyof typeof AUDIT_STATUS_COLOR_CODES] || 'text-dark-gray'}`}>{AUDIT_STATUS_LABELS[status as keyof typeof AUDIT_STATUS_LABELS] || status}</div>
          </OverlayTrigger>
        )}
      </div>
    );
  }

  const [tableConfig] = useState({
    paginationMode: "remote",
    ajaxRequestFunc,
    columns,
    rowHeight: 48,
    selectable: false ,
    paginate: true,
    initialSort: ["month desc"],
    rowFormatter,
  });

  function rowFormatter(row: any) {
    const data = row.getData();
    const element = row.getElement();
    if (data.published) {
      // element.classList.add("activity-published");
    }
  }

  function formatApiResponse(params: any, list: any, totalRecords: number) {
    const { page, per } = params || {};
    const tdata = {
      data: list,
      total: totalRecords,
      last_page: Math.ceil(totalRecords / (per || 1)) || 1,
      page: page || 1,
    };
    return tdata;
  }

  function hasFilters(ref: any, field = "company_id_eq") {
    const _filters: any =
      (ref
        ? ref.current
        : ({ ...(payloadRef.current || {}) } as any)["filters"]) || {};
    return _filters[field];
  }

  function getStatusFilters() {
    const selected = auditStatusRef.current || {};
    const selectedStatuses = AUDIT_STATUS_FILTER_OPTIONS
      .filter(opt => selected[opt.value])
      .flatMap((opt: any) => opt.statuses);
    const filters: any = selectedStatuses.length > 0
      ? { status_in: selectedStatuses }
      : { status_not_eq: VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_CREATED };

    if (monthRef.current && monthRef.current.value && monthRef.current.value !== "all") {
      filters.month_eq = monthRef.current.value;
    }
    if (yearRef.current && yearRef.current.value && yearRef.current.value !== "all") {
      filters.year_eq = String(yearRef.current.value);
    }
    return filters;
  }

  function ajaxRequestFunc(url: any, config: any, params: any) {
    const { field, dir } = (params.sort || [])[0] || {};
    const _params = {
      per: params.size,
      page: params.page,
      include: ['company', 'associate_company', 'location', 'vendor_category', 'vendor_registration', 'preliminary_report', 'final_report'],
      includes: ['company', 'associate_company', 'location', 'vendor_category', 'vendor_registration', 'preliminary_report', 'final_report']
    };
    setParams(_params);
    setPayload({
      ...DEFAULT_PAYLOAD,
      ..._params,
      filters: {
        ...(lfRef.current || {}),
        ...getStatusFilters(),
      },
    });
    return Promise.resolve(formatApiResponse(params, activities, total));
  }

  function handlePageNav(_pagination: any) {
    const _params = { ...params, ..._pagination };
    setParams({ ..._params });
    setPayload({ ...(payload as any), ..._params });
  }

  useEffect(() => {
    if (locationFilters) {
      setPayload({
        ...DEFAULT_PAYLOAD,
        ...params,
        filters: {
          ...locationFilters,
          ...getStatusFilters(),
        },
      });
    }
  }, [locationFilters, auditStatusFilter, monthFilter, yearFilter]);

  useEffect(() => {
    if (!isFetching && payload) {
      setData(formatApiResponse(params, activities, total));
    }
  }, [isFetching]);

  function rowClick(e:any, selected: any) {
    const audit = selected.getData() || {};
    if (audit.id) {
      navigate(`/vendor-audits/${audit.id}`, {
        state: {
          ...(previousFiltersRef.current || {}),
          initialFilters: lfRef.current,
          auditStatusFilter: auditStatusRef.current,
          month: monthRef.current && monthRef.current.value,
          year: yearRef.current && yearRef.current.value,
        }
      });
    }
  }

  function openBulkDownloadModal() {
    setEnableBulkDownloadModal(true)
  }

  return (
    <>
      <div className="d-flex flex-column">
        <div className="d-flex  p-2 align-items-center pageHeading shadow">
          <h4 className="mb-0">Vendor Audits</h4>
          <div className="d-flex align-items-end h-100">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0 d-flex justify-content-end">
                <li className="breadcrumb-item">Home</li>
                {fromDashboard && (
                  <li className="breadcrumb-item fw-bold">
                    <Link href="/dashboard">Dashboard</Link>
                  </li>
                )}
                <li className="breadcrumb-item  fw-bold active">
                  Audit Compliances
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <form className="p-0 mx-3">
          <div className="card shadow border-0 p-2 mt-2 mb-1">
            <div className="d-flex justify-content-between">
              <div className="d-flex flex-row align-items-center filters">
                <AdminVendorLocations onChange={onLocationChange} previousFilters={previousFilters} />
                <div className="px-2">
                  <label className="filter-label">
                    <small>Month</small>
                  </label>
                  <Select
                    options={[ALL_OPTION, ...MONTHS]}
                    onChange={(val) => setMonthFilter(val)}
                    value={monthFilter}
                    className="select-control"
                  />
                </div>
                <div className="px-2">
                  <label className="filter-label">
                    <small>Year</small>
                  </label>
                  <Select
                    options={[ALL_OPTION, ...yearOptions]}
                    onChange={(val) => setYearFilter(val)}
                    value={yearFilter}
                    className="select-control"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card border-0 mt-1 mb-2" style={{ padding: '16px', borderRadius: '4px', background: '#FFF', boxShadow: '0px 16px 32px 0px rgba(12,12,13,0.1), 0px 4px 4px 0px rgba(12,12,13,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch' }}>
              <div className="d-flex status-btn-group flex-wrap align-items-center" style={{ gap: '8px' }}>
                <small className="me-1" style={{ color: '#3261a9', fontWeight: 600, whiteSpace: 'nowrap' }}>Status</small>
                {AUDIT_STATUS_FILTER_OPTIONS.map((opt) => {
                  const isSelected = !!(auditStatusFilter && auditStatusFilter[opt.value]);
                  return (
                    <div key={opt.value}>
                      <input
                        type="checkbox"
                        className="btn-check"
                        name={opt.value}
                        id={`audit-status-${opt.value}`}
                        autoComplete="off"
                        checked={isSelected}
                        onChange={(e) => setAuditStatusFilter((prev: any) => ({ ...prev, [opt.value]: e.target.checked }))}
                      />
                      <label
                        className={`btn btn-sm bg-audit-status-${opt.value}`}
                        htmlFor={`audit-status-${opt.value}`}
                        // style={{ opacity: isSelected ? 1 : 0.65, transition: 'opacity 0.15s ease' }}
                      >
                        {opt.label}
                      </label>
                    </div>
                  );
                })}
              </div>
              {(hasUserAccess(USER_PRIVILEGES.REVIEWER_VENDOR_ACTIVITIES_DOWNLOAD_BULK_REPORT) ||
                hasUserAccess(USER_PRIVILEGES.SUBMITTER_VENDOR_ACTIVITIES_DOWNLOAD_BULK_REPORT)) && (
                <button
                  type="button"
                  className="btn btn-sm ms-2"
                  onClick={openBulkDownloadModal}
                  style={{
                    backgroundColor: '#2965ad',
                    border: '2px solid #477dba',
                    boxShadow: '0px 5px 0px 0px #2061b1',
                    color: 'white',
                    fontWeight: 700,
                    borderRadius: '7px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Bulk Report
                </button>
              )}
            </div>
          </div>
        </form>
        <div className="position-relative clickable">
          <Table
            data={data}
            options={tableConfig}
            onPageNav={handlePageNav}
            rowClick={rowClick}
          />
        </div>
      </div>
      {enableBulkDownloadModal && (
        <VendorAuditBulkDownloadModal
        onSubmit={() => setEnableBulkDownloadModal(false)}
        onCancel={() => { setEnableBulkDownloadModal(false) }}
        previousFilters={previousFilters} />
      )}
      {dropAudit && (
        <DropAuditModal
          audit={dropAudit}
          onClose={() => setDropAudit(null)}
          onSuccess={() => {
            setDropAudit(null);
            refetch();
          }}
        />
      )}
      {(showLoader || isFetching) && <PageLoader />}
    </>
  );
}

export default VendorAuditList;
